/**
 * Brand page (sections/brand-page.liquid).
 *
 * Range tiles
 * - Picking a tile shows its panel (Section Rendering API:
 *   /collections/{range}?section_id=brand-mallisto-grid, cached, grey
 *   placeholder cards while loading) and filters the product listing below
 *   to that range with the Search & Discovery "Mallisto" filter
 *   (filter.p.m.custom.mallisto = the tile's data-filter-value).
 * - Picking the selected tile again, "Kaikki mallistot" or the hero button
 *   deselects: the panel band closes, the Mallisto filter, ?mallisto= and
 *   ?tyyppi= are removed. With nothing selected the band stays closed.
 * - ?mallisto= only accepts a range: a tile's handle or "{brand handle}-…".
 *   The brand's own collection or anything else is dropped from the URL.
 * - The listing is updated through the theme's own filter code
 *   (facets-form-component → updateFiltersByURL → section re-render), so the
 *   sidebar, active-filter pills, counts and "Clear all" stay correct. Other
 *   filters and sorting are kept; the page goes back to 1.
 * - The other way round: when the Mallisto filter changes in the sidebar
 *   (checkbox, pill, "Clear all") or on back/forward, the tiles follow. One
 *   value selects that tile; none or several select nothing.
 * - The URL carries ?mallisto={range handle} next to the filter, and
 *   ?tyyppi={category} for the panel's chip; a shared URL restores the state.
 *
 * Row: one line that scrolls sideways; ‹ › (on every screen size) when the
 * tiles don't all fit, a vertical mouse wheel scrolls it too. It glides along
 * slowly and without stopping, like the home page logo strip (which also
 * keeps moving with reduced motion on): copies of the tiles follow the
 * originals (and show the selection too) so it loops without a jump. It
 * pauses under the mouse, with keyboard focus, for a while after the visitor
 * picks a tile or scrolls the row, off screen and in a background tab. A newly
 * selected tile is scrolled into view first.
 *
 * Without a product listing on the page (no facets form), tiles only drive
 * the panel and the URL, and the hero button is a plain link.
 *
 * Panel chips (Takit, Housut …) only switch the panel, never the listing.
 */

import { sectionRenderer } from '@theme/section-renderer';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';

const SECTION_ID = 'brand-mallisto-grid';
const FILTER = 'filter.p.m.custom.mallisto';
/** Glide speed of the tile row, px per second (slow and steady, like the home page logo strip) */
const AUTOPLAY_SPEED = 28;
const AUTOPLAY_RESUME = 8000;
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const DEV =
  ['127.0.0.1', 'localhost'].includes(window.location.hostname) ||
  new URLSearchParams(window.location.search).has('preview_theme_id') ||
  Boolean(window.Shopify?.designMode);

/**
 * What the listing depends on: the URL without the brand page's own params.
 * @param {string} href
 */
function listingKey(href) {
  const url = new URL(href, window.location.origin);
  for (const key of ['mallisto', 'tyyppi', 'section_id']) url.searchParams.delete(key);
  url.searchParams.sort();
  return url.pathname + url.search;
}

class BrandPage {
  /** @type {Map<string, Promise<string>>} panel HTML per range handle */
  cache = new Map();
  request = 0;
  /** @type {HTMLAnchorElement | null} */
  selected = null;
  /** @type {number | null} where an arrow-started smooth scroll is headed */
  scrollTarget = null;
  /** The listing is being updated by a tile: ignore its own FilterUpdate. */
  ownUpdate = false;
  listingKey = listingKey(window.location.href);
  hovered = false;
  /** @type {HTMLElement[] | null} copies of the tiles that make the gliding row loop */
  clones = null;
  /** Width of one set of tiles (where the copies start) */
  setLength = 0;
  onScreen = true;
  lastInteraction = 0;
  /** Handle of the range in the panel ('' while none is loaded) */
  handle = '';
  bandTimer = 0;

  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.host = /** @type {HTMLElement} */ (root.querySelector('[data-bp-host]'));
    this.tiles = /** @type {HTMLAnchorElement[]} */ ([...root.querySelectorAll('[data-bp-tile]')]);
    if (!this.host || !this.tiles.length) return;

    this.band = /** @type {HTMLElement | null} */ (root.querySelector('[data-bp-band]'));
    this.brandHandle = root.dataset.brandHandle || '';

    const panel = this.host.querySelector('[data-bp-panel]');
    if (panel instanceof HTMLElement) {
      this.handle = panel.dataset.handle || '';
      this.cache.set(this.handle, Promise.resolve(panel.outerHTML));
      this.#warnOverLimit(panel);
    }

    this.track = /** @type {HTMLElement} */ (root.querySelector('[data-bp-tiles]'));
    this.nav = /** @type {HTMLElement | null} */ (root.querySelector('[data-bp-nav]'));
    this.prev = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-bp-prev]'));
    this.next = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-bp-next]'));
    this.clearButton = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-bp-clear]'));
    this.selected = this.tiles.find((t) => t.getAttribute('aria-selected') === 'true') || null;

    this.track.addEventListener('click', this.#onTileClick);
    this.track.addEventListener('keydown', this.#onTileKeyDown);
    this.track.addEventListener('scroll', this.#updateArrows, { passive: true });
    this.track.addEventListener('wheel', this.#onWheel, { passive: false });
    for (const type of ['touchstart', 'pointerdown']) {
      this.track.addEventListener(type, this.#onUserScroll, { passive: true });
    }
    new ResizeObserver(this.#updateArrows).observe(this.track);
    this.prev?.addEventListener('click', () => this.#arrow(-1));
    this.next?.addEventListener('click', () => this.#arrow(1));
    this.clearButton?.addEventListener('click', () => this.deselect());
    root.querySelector('[data-bp-show-all]')?.addEventListener('click', this.#onShowAll);
    this.host.addEventListener('click', this.#onChipClick);

    document.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    window.addEventListener('popstate', this.#onPopState);

    this.#updateArrows();
    this.#restoreFromUrl();
    this.#startAutoplay();
  }

  /* ── Selection ───────────────────────────────────────────── */

  /**
   * Select a tile: highlight it, show its panel and filter the listing.
   * @param {HTMLAnchorElement} tile
   * @param {{ push?: boolean, instant?: boolean, chip?: string, focus?: boolean }} [options]
   *   push: add a history entry (a click) or replace the current one (page load)
   */
  selectTile(tile, { push = true, instant = false, chip = '', focus = false } = {}) {
    this.#highlight(tile);
    this.#revealTile(tile, instant);
    this.#applyFilter(tile, push);
    // The panel (or its placeholder) goes in first, so the band opens to its height.
    this.#showPanel(tile.dataset.handle || '', tile.href, { tile, chip, focus });
    this.#setBand(true);
  }

  /**
   * No tile selected: Mallisto filter off, ?mallisto= and ?tyyppi= gone, and
   * the range panel closes (it keeps its content for when it reopens).
   */
  deselect({ push = true } = {}) {
    this.#highlight(null);
    this.#applyFilter(null, push);
    this.#setBand(false);
  }

  /**
   * A handle that may be shown as a range: one of the tiles, or a collection
   * named "{brand handle}-…". Never the brand's own collection.
   * @param {string} handle
   */
  #isRangeHandle(handle) {
    if (!/^[\w-]+$/.test(handle) || handle === this.brandHandle) return false;
    if (this.#tileByHandle(handle)) return true;
    return Boolean(this.brandHandle) && handle.startsWith(`${this.brandHandle}-`);
  }

  /** Drop ?mallisto= and ?tyyppi= from the URL without a new history entry. */
  #clearRangeParams() {
    const url = new URL(window.location.href);
    url.searchParams.delete('mallisto');
    url.searchParams.delete('tyyppi');
    if (url.href !== window.location.href) history.replaceState(history.state, '', url);
  }

  /**
   * Open or close the range panel band. Its height animates between 0 and its
   * content, so the page below slides instead of jumping.
   * @param {boolean} show
   */
  #setBand(show) {
    const band = this.band;
    if (!band) return;
    const isOpen = !band.hidden && band.dataset.closing === undefined;
    if (show === isOpen) return;

    const finish = () => {
      band.style.height = '';
      band.style.overflow = '';
      band.style.transition = '';
      if (band.dataset.closing !== undefined) band.hidden = true;
      delete band.dataset.closing;
    };

    if (REDUCED_MOTION.matches) {
      band.hidden = !show;
      delete band.dataset.closing;
      return;
    }

    band.style.overflow = 'hidden';
    if (show) {
      delete band.dataset.closing;
      band.hidden = false;
      const from = band.style.height ? band.offsetHeight : 0;
      band.style.transition = 'none';
      band.style.height = `${from}px`;
      void band.offsetHeight;
      band.style.transition = 'height 0.3s ease';
      band.style.height = `${band.scrollHeight}px`;
    } else {
      band.dataset.closing = '';
      band.style.transition = 'none';
      band.style.height = `${band.offsetHeight}px`;
      void band.offsetHeight;
      band.style.transition = 'height 0.3s ease';
      band.style.height = '0px';
    }

    clearTimeout(this.bandTimer);
    this.bandTimer = window.setTimeout(finish, 320);
  }

  /**
   * More than 250 products: the panel's chips are plain links without counts.
   * @param {Element} panel
   */
  #warnOverLimit(panel) {
    const count = panel.getAttribute('data-over-limit');
    if (count && DEV) {
      console.warn(
        `[brand-page] ${panel.getAttribute('data-handle')}: ${count} products, more than the 250 the panel can read. ` +
          'Chips link to the categories without counts.'
      );
    }
  }

  /** @param {HTMLAnchorElement | null} tile */
  #highlight(tile) {
    this.selected = tile;
    this.tiles.forEach((t, i) => {
      t.setAttribute('aria-selected', String(t === tile));
      // One tile stays in the tab order: the selected one, else the first.
      t.tabIndex = t === tile || (!tile && i === 0) ? 0 : -1;
    });
    // Copies look the same as their originals
    this.clones?.forEach((clone) => {
      clone.setAttribute('aria-selected', String(Boolean(tile) && clone.dataset.handle === tile?.dataset.handle));
    });
    if (this.clearButton) this.clearButton.hidden = !tile;
    if (tile) this.host.setAttribute('aria-labelledby', tile.id);
    else this.host.removeAttribute('aria-labelledby');
  }

  /**
   * Point the listing's Mallisto filter at the tile's value (replacing any
   * other value), or remove it. Goes through the theme's facets form so the
   * sidebar, pills and counts re-render as with any other filter.
   * @param {HTMLAnchorElement | null} tile
   * @param {boolean} push
   */
  #applyFilter(tile, push) {
    const value = tile?.dataset.filterValue || '';
    if (tile && !value && DEV) {
      console.warn(`[brand-page] ${tile.dataset.handle}: no custom.mallisto value found, the listing isn't filtered.`);
    }

    const url = new URL(window.location.href);
    const before = url.searchParams.getAll(FILTER);
    url.searchParams.delete(FILTER);
    if (value) url.searchParams.append(FILTER, value);
    url.searchParams.delete('page');
    url.searchParams.delete('tyyppi');
    if (tile) url.searchParams.set('mallisto', tile.dataset.handle || '');
    else url.searchParams.delete('mallisto');

    const unchanged = before.length === (value ? 1 : 0) && (!value || before[0] === value);
    const facetsForm = document.querySelector('facets-form-component');

    if (unchanged || !facetsForm) {
      history.replaceState(history.state, '', url);
      return;
    }

    this.ownUpdate = true;
    try {
      if (push) {
        // The same call the active-filter pills use: pushState + re-render.
        /** @type {any} */ (facetsForm).updateFiltersByURL(url.pathname + url.search);
      } else {
        history.replaceState(history.state, '', url);
        this.#renderListing(facetsForm);
      }
    } finally {
      this.ownUpdate = false;
    }
    this.listingKey = listingKey(window.location.href);
  }

  /**
   * Re-render the listing for the current URL without a new history entry
   * (page load, back/forward), the way the facets form does it.
   * @param {Element} facetsForm
   */
  #renderListing(facetsForm) {
    const params = new URL(window.location.href).searchParams;
    facetsForm.dispatchEvent(new FilterUpdateEvent(params));
    const sectionId = facetsForm.getAttribute('section-id');
    if (sectionId) sectionRenderer.renderSection(sectionId);
  }

  /** @param {string} value */
  #tileByValue(value) {
    return this.tiles.find((t) => t.dataset.filterValue && t.dataset.filterValue === value) || null;
  }

  /** @param {string} handle */
  #tileByHandle(handle) {
    return this.tiles.find((t) => t.dataset.handle === handle) || null;
  }

  /**
   * Bring tiles and panel in line with the URL (sidebar change, back/forward).
   * Exactly one Mallisto value selects its tile; none or several, nothing —
   * except a range without a filter value, which only ?mallisto= can carry.
   */
  #syncFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const values = params.getAll(FILTER);
    const handle = params.get('mallisto') || '';
    let tile = null;
    if (values.length === 1) tile = this.#tileByValue(values[0]);
    else if (values.length === 0) {
      const byHandle = this.#tileByHandle(handle);
      if (byHandle && !byHandle.dataset.filterValue) tile = byHandle;
    }
    // A range without a tile, linked by ?mallisto= only (e.g. from a product page)
    const rangeOnly = !tile && values.length === 0 && !this.#tileByHandle(handle) && this.#isRangeHandle(handle);

    if (tile !== this.selected) {
      this.#highlight(tile);
      if (tile) {
        this.#revealTile(tile, false);
        this.#showPanel(tile.dataset.handle || '', tile.href, { tile, chip: params.get('tyyppi') || '' });
      }
    }
    this.#setBand(Boolean(tile) || rangeOnly);

    // Keep ?mallisto= matching the selection (the filter wins); nothing
    // selected → no range params at all.
    const url = new URL(window.location.href);
    if (tile) url.searchParams.set('mallisto', tile.dataset.handle || '');
    else if (!rangeOnly) {
      url.searchParams.delete('mallisto');
      url.searchParams.delete('tyyppi');
    }
    if (url.href !== window.location.href) history.replaceState(history.state, '', url);
  }

  #restoreFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const values = params.getAll(FILTER);
    const handle = params.get('mallisto') || '';
    const chip = params.get('tyyppi') || '';

    if (values.length) {
      // The filter wins over ?mallisto=. Liquid already selected the tile and
      // rendered its panel; this fixes up the URL and the row position.
      this.#syncFromUrl();
      if (this.selected) this.#revealTile(this.selected, true);
      if (chip) this.setChip(chip);
      return;
    }

    if (handle && this.#isRangeHandle(handle)) {
      const tile = this.#tileByHandle(handle);
      if (tile) {
        this.selectTile(tile, { push: false, instant: true, chip });
        return;
      }
      // A range without a tile (e.g. linked from a product page): panel only.
      this.#showPanel(handle, `${window.Shopify?.routes?.root || '/'}collections/${encodeURIComponent(handle)}`, {
        chip,
      });
      this.#setBand(true);
      return;
    }

    // No range (or the brand itself, or nonsense): no panel, no range params.
    this.#setBand(false);
    this.#clearRangeParams();
  }

  /* ── Panel ───────────────────────────────────────────────── */

  /**
   * @param {string} handle
   * @param {string} href - The range's collection URL (keeps the locale prefix)
   * @param {{ tile?: HTMLAnchorElement | null, chip?: string, focus?: boolean }} [options]
   */
  async #showPanel(handle, href, { tile = null, chip = '', focus = false } = {}) {
    if (handle === this.handle) {
      this.setChip(chip);
      if (focus) this.#focusTitle();
      return;
    }

    this.handle = handle;
    const token = ++this.request;
    this.#showPlaceholder();

    let html;
    try {
      html = await this.#load(handle, href);
    } catch {
      if (token !== this.request) return;
      this.handle = '';
      if (tile) {
        // No panel to show: go to the range's own page instead.
        window.location.href = href;
      } else {
        // ?mallisto= named a collection that isn't a range: drop it.
        this.host.replaceChildren();
        this.#setBand(false);
        this.#clearRangeParams();
      }
      return;
    }
    if (token !== this.request) return;

    const template = document.createElement('template');
    template.innerHTML = html;
    const panel = template.content.firstElementChild;
    if (!panel) return;

    this.host.replaceChildren(panel);
    this.host.removeAttribute('aria-busy');
    this.host.style.minHeight = '';
    this.#warnOverLimit(panel);
    // The band opened at the placeholder's height; follow the real content.
    if (this.band?.style.height && this.band.dataset.closing === undefined) {
      this.band.style.height = `${this.band.scrollHeight}px`;
    }
    if (tile) this.#applyTileText(tile, panel);
    this.setChip(chip);
    if (focus) this.#focusTitle();
  }

  #focusTitle() {
    /** @type {HTMLElement | null} */ (this.host.querySelector('[data-bp-title]'))?.focus({ preventScroll: true });
  }

  /** @param {string} key - Category collection handle, '' for "Kaikki" */
  setChip(key) {
    const views = /** @type {HTMLElement[]} */ ([...this.host.querySelectorAll('[data-bp-view]')]);
    const view = views.find((v) => v.dataset.bpView === key) || views.find((v) => v.dataset.bpView === '');
    if (!view) return;
    const active = view.dataset.bpView || '';

    views.forEach((v) => (v.hidden = v !== view));
    this.host.querySelectorAll('[data-bp-chip]').forEach((c) => {
      c.setAttribute('aria-pressed', String(c.getAttribute('data-bp-chip') === active));
    });

    const all = this.host.querySelector('[data-bp-all]');
    if (all instanceof HTMLAnchorElement && view.dataset.url) all.href = view.dataset.url;
    const count = this.host.querySelector('[data-bp-all-count]');
    if (count && view.dataset.count) count.textContent = view.dataset.count;
    // "Näytä kaikki 6 DX4-tuotetta: Housut →" while a category chip is on
    const label = this.host.querySelector('[data-bp-all-label]');
    if (label) label.textContent = view.dataset.label ? `: ${view.dataset.label}` : '';

    // The chip only lives in the URL; it never touches the listing.
    const url = new URL(window.location.href);
    if (active) url.searchParams.set('tyyppi', active);
    else url.searchParams.delete('tyyppi');
    if (url.href !== window.location.href) history.replaceState(history.state, '', url);
  }

  /**
   * @param {string} handle
   * @param {string} href
   * @returns {Promise<string>}
   */
  #load(handle, href) {
    let request = this.cache.get(handle);
    if (!request) {
      const url = new URL(href, window.location.origin);
      url.searchParams.set('section_id', SECTION_ID);
      request = fetch(url)
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((text) => {
          const panel = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-bp-panel]');
          return panel ? panel.outerHTML : Promise.reject(new Error('No panel'));
        });
      this.cache.set(handle, request);
      request.catch(() => this.cache.delete(handle));
    }
    return request;
  }

  /** Grey cards in place of the grid, at the current height so nothing jumps. */
  #showPlaceholder() {
    this.host.style.minHeight = `${this.host.offsetHeight}px`;
    this.host.setAttribute('aria-busy', 'true');
    const views = this.host.querySelector('[data-bp-views]');
    const template = this.host.querySelector('template[data-bp-placeholder]');
    if (views && template instanceof HTMLTemplateElement) {
      views.replaceChildren(template.content.cloneNode(true));
    } else {
      // Nothing loaded yet (no range was selected on page load).
      const card =
        '<li class="bp-grid__item bp-grid__item--placeholder" aria-hidden="true">' +
        '<span class="bp-ph__media"></span><span class="bp-ph__line"></span>' +
        '<span class="bp-ph__line bp-ph__line--short"></span></li>';
      this.host.innerHTML =
        `<div class="bp-panel"><ul class="bp-grid" role="list" aria-busy="true">${card.repeat(3)}` +
        '<li class="bp-grid__item bp-grid__item--more bp-grid__item--placeholder" aria-hidden="true"></li></ul></div>';
    }
    this.host.querySelectorAll('[data-bp-chip]').forEach((c) => c.setAttribute('disabled', ''));
  }

  /**
   * The tile's own description (set on its block) wins over the collection's.
   * @param {HTMLElement} tile
   * @param {Element} panel
   */
  #applyTileText(tile, panel) {
    const description = tile.dataset.description;
    const target = panel.querySelector('[data-bp-desc]');
    if (description && target instanceof HTMLElement) {
      target.textContent = description;
      target.hidden = false;
    }
  }

  /* ── Row scrolling ───────────────────────────────────────── */

  /**
   * Scroll by a few tiles: half of what fits, at least 2 (desktop 3, tablet
   * and mobile 2). Counts from where a running smooth scroll is headed, so
   * quick repeated clicks add up. With the looping copies in place the row
   * never runs out: near either end it first steps one set back or forward,
   * which looks identical.
   * @param {number} direction - -1 for ‹, 1 for ›
   */
  #scrollTiles(direction) {
    const track = this.track;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const tileStep = (this.tiles[0]?.offsetWidth || 0) + gap;
    if (!tileStep) return;
    const perClick = Math.max(2, Math.floor(track.clientWidth / tileStep / 2));
    const step = tileStep * perClick;
    const max = track.scrollWidth - track.clientWidth;
    let from = this.scrollTarget ?? track.scrollLeft;

    if (this.setLength > 0) {
      if (direction > 0 && from + step > max) {
        track.scrollLeft -= this.setLength;
        from -= this.setLength;
      } else if (direction < 0 && from - step < 0) {
        track.scrollLeft += this.setLength;
        from += this.setLength;
      }
    }

    // Count from the nearest tile edge (the row may have glided to mid-tile),
    // so the step lands on a tile and snapping doesn't eat part of it.
    const first = this.tiles[0];
    const pad = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
    const origin = (first?.offsetLeft || 0) - pad;
    const index = Math.round((from - origin) / tileStep);
    this.scrollTarget = Math.min(max, Math.max(0, origin + (index + direction * perClick) * tileStep));
    track.scrollTo({ left: this.scrollTarget, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
  }

  /** @param {number} direction */
  #arrow(direction) {
    this.lastInteraction = Date.now();
    this.#stopGliding();
    this.#scrollTiles(direction);
  }

  /**
   * Scroll the row (not the page) so the tile is fully visible.
   * @param {HTMLElement} tile
   * @param {boolean} instant
   */
  #revealTile(tile, instant) {
    const track = this.track;
    this.#stopGliding();
    // Hold the row still for a moment so the selection can be seen, then glide on.
    this.lastInteraction = Date.now();
    // Glided into the copies: step back one set, which looks identical, so the
    // original tile sits where its copy was.
    if (this.setLength > 0 && track.scrollLeft >= this.setLength) track.scrollLeft -= this.setLength;
    const pad = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
    const start = tile.offsetLeft - pad;
    const end = tile.offsetLeft + tile.offsetWidth + pad - track.clientWidth;
    let left;
    if (start < track.scrollLeft) left = start;
    else if (end > track.scrollLeft) left = end;
    else return;
    this.scrollTarget = null;
    if (instant || REDUCED_MOTION.matches) track.scrollLeft = left;
    else track.scrollTo({ left, behavior: 'smooth' });
  }

  /**
   * A vertical mouse wheel over the row scrolls it sideways; at either end the
   * page scrolls on as usual. Horizontal trackpad swipes and Shift+wheel
   * scroll the row natively and are left alone.
   * @param {WheelEvent} event
   */
  #onWheel = (event) => {
    this.#onUserScroll();
    if (event.ctrlKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
    const track = this.track;
    const max = track.scrollWidth - track.clientWidth;
    if (max <= 1) return;
    const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
    if ((delta < 0 && track.scrollLeft <= 0) || (delta > 0 && track.scrollLeft >= max - 1)) return;
    event.preventDefault();
    track.scrollBy({ left: delta });
  };

  /** The visitor moves the row themselves: forget the arrows' target, pause autoplay. */
  #onUserScroll = () => {
    this.scrollTarget = null;
    this.lastInteraction = Date.now();
    this.#stopGliding();
  };

  /**
   * Arrows only when the tiles don't all fit. With the looping copies they
   * never run out; without them they're disabled at either end.
   */
  #updateArrows = () => {
    if (this.scrollTarget != null && Math.abs(this.track.scrollLeft - this.scrollTarget) < 2) this.scrollTarget = null;
    if (!this.nav) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const looping = this.setLength > 0;
    this.nav.hidden = !looping && max <= 1;
    if (this.prev) this.prev.disabled = !looping && this.track.scrollLeft <= 1;
    if (this.next) this.next.disabled = !looping && this.track.scrollLeft >= max - 1;
  };

  /** Width of the original tiles in a row, gaps included. */
  #tilesWidth() {
    const first = this.tiles[0];
    const last = this.tiles[this.tiles.length - 1];
    if (!first || !last) return 0;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return last.offsetLeft + last.offsetWidth - first.offsetLeft + gap;
  }

  /**
   * Copies of the tiles after the originals, only while the originals don't
   * all fit: when the row has glided one full set along it jumps back by
   * exactly that much, which looks the same. Re-checked on every resize, so it
   * also works when the row had no width yet when the page loaded.
   */
  #syncClones = () => {
    const track = this.track;
    const overflowing = track.clientWidth > 0 && this.#tilesWidth() > track.clientWidth + 1;

    if (overflowing && !this.clones) {
      this.clones = this.tiles.map((tile) => {
        const clone = /** @type {HTMLElement} */ (tile.cloneNode(true));
        clone.removeAttribute('id');
        clone.removeAttribute('data-bp-tile');
        for (const { name } of [...clone.attributes]) {
          if (name.startsWith('data-shopify')) clone.removeAttribute(name);
        }
        clone.setAttribute('aria-hidden', 'true');
        clone.tabIndex = -1;
        clone.dataset.bpClone = '';
        track.append(clone);
        return clone;
      });
    } else if (!overflowing && this.clones) {
      this.clones.forEach((clone) => clone.remove());
      this.clones = null;
    }

    this.setLength = this.clones ? this.clones[0].offsetLeft - this.tiles[0].offsetLeft : 0;
    this.#updateArrows();
  };

  /**
   * The row glides along slowly and constantly, like the home page logo strip
   * (which also keeps moving with the device's reduced-motion setting on).
   * Paused under the mouse, with keyboard focus in the row, for a while after
   * the visitor picks a tile or scrolls the row, off screen and in a
   * background tab.
   */
  #startAutoplay() {
    const track = this.track;
    this.#syncClones();
    new ResizeObserver(this.#syncClones).observe(track);

    const area = this.root.querySelector('.bp-ranges') || track;
    area.addEventListener('pointerenter', (e) => {
      if (/** @type {PointerEvent} */ (e).pointerType === 'mouse') this.hovered = true;
    });
    area.addEventListener('pointerleave', () => (this.hovered = false));
    new IntersectionObserver(([entry]) => (this.onScreen = Boolean(entry?.isIntersecting))).observe(track);

    let position = track.scrollLeft;
    let last = performance.now();
    const frame = (/** @type {number} */ now) => {
      const elapsed = Math.min(now - last, 100);
      last = now;
      const moving =
        this.setLength > 0 &&
        !this.hovered &&
        this.onScreen &&
        !document.hidden &&
        !this.root.querySelector(':focus-visible') &&
        Date.now() - this.lastInteraction >= AUTOPLAY_RESUME;

      if (moving) {
        if (!track.classList.contains('is-gliding')) {
          // Snapping off while gliding, or it would pull the row back to a tile.
          track.classList.add('is-gliding');
          position = track.scrollLeft;
        }
        // Moved by something else (a resize, the browser): carry on from there.
        if (Math.abs(track.scrollLeft - position) > 2) position = track.scrollLeft;
        position += (AUTOPLAY_SPEED * elapsed) / 1000;
        if (position >= this.setLength) position -= this.setLength;
        track.scrollLeft = position;
      } else {
        position = track.scrollLeft;
      }
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }

  /** Back to normal snapping, e.g. when the visitor takes over the row. */
  #stopGliding() {
    this.track.classList.remove('is-gliding');
  }

  /* ── Events ──────────────────────────────────────────────── */

  /** @param {HTMLAnchorElement} tile */
  #toggle(tile, focus = false) {
    this.lastInteraction = Date.now();
    if (tile === this.selected) this.deselect();
    else this.selectTile(tile, { focus });
  }

  /** @param {MouseEvent} event */
  #onTileClick = (event) => {
    const hit = /** @type {Element} */ (event.target).closest('[data-bp-tile], [data-bp-clone]');
    if (!(hit instanceof HTMLAnchorElement)) return;
    // A copy in the gliding row stands for its original tile.
    const tile = hit.hasAttribute('data-bp-clone') ? this.#tileByHandle(hit.dataset.handle || '') : hit;
    if (!tile) return;
    // Let ctrl/cmd/shift-click open the range's page as a normal link.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.#toggle(tile);
  };

  /** @param {KeyboardEvent} event */
  #onTileKeyDown = (event) => {
    const tile = /** @type {Element} */ (event.target).closest('[data-bp-tile]');
    if (!(tile instanceof HTMLAnchorElement)) return;
    const index = this.tiles.indexOf(tile);

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.#toggle(tile, true);
      return;
    }

    let next = -1;
    if (event.key === 'ArrowRight') next = (index + 1) % this.tiles.length;
    if (event.key === 'ArrowLeft') next = (index - 1 + this.tiles.length) % this.tiles.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.tiles.length - 1;
    if (next < 0) return;

    event.preventDefault();
    this.tiles.forEach((t, i) => (t.tabIndex = i === next ? 0 : -1));
    this.tiles[next]?.focus();
  };

  /** @param {MouseEvent} event */
  #onChipClick = (event) => {
    const chip = /** @type {Element} */ (event.target).closest('[data-bp-chip]');
    if (!chip) return;
    this.setChip(chip.getAttribute('data-bp-chip') || '');
  };

  /** "Kaikki {Merkki}-tuotteet": everything from the brand, then down to the list. */
  #onShowAll = (/** @type {MouseEvent} */ event) => {
    // No listing on this page: the button is a plain link to all products.
    const listing = document.getElementById('ResultsList');
    if (!listing) return;
    event.preventDefault();
    this.deselect();
    listing.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
  };

  /** A filter changed in the sidebar (checkbox, pill, "Clear all", sorting). */
  #onFilterUpdate = () => {
    if (this.ownUpdate) return;
    this.listingKey = listingKey(window.location.href);
    this.#syncFromUrl();
  };

  /** Back/forward: re-render the listing if its filters differ, then the tiles. */
  #onPopState = () => {
    const key = listingKey(window.location.href);
    if (key !== this.listingKey) {
      this.listingKey = key;
      const facetsForm = document.querySelector('facets-form-component');
      if (facetsForm) {
        this.ownUpdate = true;
        try {
          this.#renderListing(facetsForm);
        } finally {
          this.ownUpdate = false;
        }
      }
    }
    this.#syncFromUrl();
  };
}

document.querySelectorAll('[data-bp]').forEach((root) => {
  if (root instanceof HTMLElement) new BrandPage(root);
});
